import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { usePayments } from '@/hooks/use-payments';
import { PaymentRecord } from '@/types/payment';
import { toast } from 'sonner';

interface MarkPaidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: PaymentRecord | null;
}

export function MarkPaidDialog({ open, onOpenChange, payment }: MarkPaidDialogProps) {
  const { markPaymentAsPaid } = usePayments();
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!payment || !paymentDate || !paymentMethod) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await markPaymentAsPaid(payment.id, paymentDate, paymentMethod);
      toast.success(`Payment marked as paid for ${payment.memberName}`);
      onOpenChange(false);
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update payment');
    }
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Mark Payment as Paid</DialogTitle>
          <DialogDescription>
            Mark the payment for {payment.memberName} as paid.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-muted p-3 rounded-lg">
            <div className="text-sm font-medium">{payment.memberName}</div>
            <div className="text-xs text-muted-foreground">
              ${payment.amount} - {payment.description}
            </div>
            <div className="text-xs text-muted-foreground">
              Due: {new Date(payment.dueDate).toLocaleDateString()}
            </div>
          </div>
          
          <FormField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
          />
          
          <FormField
            label="Payment Method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            placeholder="e.g., Credit Card, Cash, Bank Transfer"
            required
          />
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Mark as Paid</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}